// components/chat/chat-input/MentionsPlugin.tsx
import { useEffect, useCallback } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  KEY_DOWN_COMMAND,
  COMMAND_PRIORITY_LOW,
  $getRoot,
  TextNode,
} from "lexical";
import { $createMentionNode } from "./MentionNode";
import type { ContextItem } from "@/components/shared/AddContextMenu";

interface MentionsPluginProps {
  onOpenContextMenu?: () => void;
  onMentionSelect?: (toolId: string) => void;
}

export function MentionsPlugin({ onOpenContextMenu, onMentionSelect }: MentionsPluginProps) {
  const [editor] = useLexicalComposerContext();

  // Listen for @ key being pressed
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Check if @ is being typed (Shift + 2 on US keyboard)
        if (event.key === "@") {
          // Use a small timeout to let the @ character be inserted first
          setTimeout(() => {
            onOpenContextMenu?.();
          }, 10);
        }
        return false; // Don't prevent default - let the @ be typed
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onOpenContextMenu]);

  return null;
}

// Helper function to insert a mention into the editor
export function createMentionInserter(editor: any) {
  return (item: ContextItem) => {
    editor.update(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      
      // Find and remove the @ trigger
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Get the current node
        const anchorNode = selection.anchor.getNode();
        const text = anchorNode.getTextContent();
        
        // If text ends with @, remove it
        if (text.endsWith("@")) {
          // Delete the @ character
          const anchorOffset = selection.anchor.offset;
          selection.anchor.offset = anchorOffset - 1;
          selection.focus.offset = anchorOffset;
          selection.removeText();
        }
        
        // Insert the mention node
        const mentionNode = $createMentionNode(item.id, item.label);
        const spaceNode = $createTextNode(" ");
        selection.insertNodes([mentionNode, spaceNode]);
        spaceNode.select();
      }
    });
  };
}